<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AudienceToggle from '@/components/home/AudienceToggle.vue';
import { TenantType } from '@/config/constants/enums';
import { COMING_SOON_FEATURES, FEATURE_ICONS, FEATURE_IDS } from '@/config/constants/home';

const { t } = useI18n();

const audience = defineModel<TenantType>('audience', { required: true });

const isFlipped = ref(audience.value === TenantType.AGENT);

watch(audience, (val) => {
    isFlipped.value = val === TenantType.AGENT;
});

const features = FEATURE_IDS.map((id, index) => ({
    id,
    number: String(index + 1).padStart(2, '0'),
    comingSoon: COMING_SOON_FEATURES.has(id),
    icon: FEATURE_ICONS[id],
}));
</script>

<template>
    <section class="features-section relative overflow-hidden bg-white dark:bg-gray-900">
        <div class="container mx-auto px-6 lg:px-8">
            <!-- Section Header -->
            <div class="mb-10 max-w-2xl lg:mb-12">
                <p class="mb-3 font-medium tracking-widest text-orange-400" style="font-size: 0.65rem; letter-spacing: 0.2em">
                    {{ t('home.features.eyebrow') }}
                </p>
                <h2 class="font-display text-2xl leading-tight font-normal tracking-tight text-teal-900 sm:text-3xl lg:text-4xl dark:text-white">
                    {{ t('home.features.heading_before') }}
                    <span class="text-orange-500 italic">{{ t('home.features.heading_accent_1') }}</span>
                    &
                    <span class="text-orange-500 italic">{{ t('home.features.heading_accent_2') }}</span>
                </h2>
            </div>

            <!-- Audience Toggle (centered above cards) -->
            <div class="mb-8 flex justify-center lg:mb-14">
                <AudienceToggle v-model="audience" />
            </div>

            <!-- Flip scene -->
            <div class="features-flip-scene">
                <div class="features-flip-card" :class="{ 'is-flipped': isFlipped }">
                    <!-- ═══════════════════════════ FRONT: Supplier ═══════════════════════════ -->
                    <div class="features-flip-face features-flip-face--front" :inert="isFlipped || undefined">
                        <div class="feature-grid">
                            <div v-for="(feature, index) in features" :key="feature.id" class="feature-card group" :style="`--card-index: ${index}`">
                                <!-- Number watermark -->
                                <span class="feature-number" aria-hidden="true">{{ feature.number }}</span>

                                <!-- Icon -->
                                <div
                                    class="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-teal-100 text-teal-600 transition-colors duration-300 group-hover:bg-teal-500 group-hover:text-white dark:bg-gray-800 dark:text-gray-300 dark:group-hover:bg-gray-500"
                                >
                                    <component :is="feature.icon" class="h-5 w-5" />
                                </div>

                                <!-- Title -->
                                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <h3 class="font-display text-lg leading-snug font-normal tracking-tight text-teal-900 dark:text-white">
                                        {{ t(`home.features.titles.${feature.id}`) }}
                                    </h3>
                                    <span
                                        v-if="feature.comingSoon"
                                        class="inline-flex items-center rounded-full border border-orange-300 bg-orange-100 px-2 py-0.5 text-[10px] font-medium tracking-wide text-orange-700 uppercase dark:border-orange-400/40 dark:bg-orange-400/10 dark:text-orange-300"
                                    >
                                        {{ t('common.coming_soon') }}
                                    </span>
                                </div>

                                <!-- Subtitle -->
                                <p class="mt-1.5 text-sm font-medium text-teal-700 dark:text-gray-300">
                                    {{ t(`home.features.supplier.${feature.id}.subtitle`) }}
                                </p>

                                <!-- Description -->
                                <p class="mt-2.5 text-[0.8rem] leading-relaxed text-teal-500 dark:text-gray-400">
                                    {{ t(`home.features.supplier.${feature.id}.description`) }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- ═══════════════════════════ BACK: Agent ═══════════════════════════ -->
                    <div class="features-flip-face features-flip-face--back" :inert="!isFlipped || undefined">
                        <div class="feature-grid feature-grid--agent">
                            <div v-for="(feature, index) in features" :key="feature.id" class="feature-card group" :style="`--card-index: ${index}`">
                                <!-- Number watermark -->
                                <span class="feature-number feature-number--agent" aria-hidden="true">{{ feature.number }}</span>

                                <!-- Icon -->
                                <div
                                    class="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-orange-100 text-orange-600 transition-colors duration-300 group-hover:bg-orange-400 group-hover:text-white dark:bg-gray-800 dark:text-gray-300 dark:group-hover:bg-gray-500"
                                >
                                    <component :is="feature.icon" class="h-5 w-5" />
                                </div>

                                <!-- Title -->
                                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                                    <h3 class="font-display text-lg leading-snug font-normal tracking-tight text-teal-900 dark:text-white">
                                        {{ t(`home.features.titles.${feature.id}`) }}
                                    </h3>
                                    <span
                                        v-if="feature.comingSoon"
                                        class="inline-flex items-center rounded-full border border-orange-300 bg-orange-100 px-2 py-0.5 text-[10px] font-medium tracking-wide text-orange-700 uppercase dark:border-orange-400/40 dark:bg-orange-400/10 dark:text-orange-300"
                                    >
                                        {{ t('common.coming_soon') }}
                                    </span>
                                </div>

                                <!-- Subtitle -->
                                <p class="mt-1.5 text-sm font-medium text-orange-700 dark:text-gray-300">
                                    {{ t(`home.features.agent.${feature.id}.subtitle`) }}
                                </p>

                                <!-- Description -->
                                <p class="mt-2.5 text-[0.8rem] leading-relaxed text-orange-500 dark:text-gray-400">
                                    {{ t(`home.features.agent.${feature.id}.description`) }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Visual Accent -->
        <div class="pointer-events-none absolute right-0 bottom-0 h-64 w-64 translate-x-1/3 translate-y-1/3">
            <div class="h-full w-full rounded-full bg-teal-100/50 blur-3xl dark:bg-gray-800/30"></div>
        </div>
    </section>
</template>
